import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Center, Loader } from '@mantine/core';
import { useMemberMeService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';

/**
 * Guard for authenticated routes. Reads /api/members/me/profile-status; if
 * <c>isComplete === false</c> redirects the user to <c>/auth/onboarding</c>.
 *
 * Used to wrap any route that should require a finished member profile (e.g.
 * the rest of the Resource Centre app post-login). Anonymous browsing is NOT
 * blocked by this — the catalogue stays open via the public endpoints.
 */
export function RequireProfileComplete({ children }: { children: ReactNode }) {
  const auth = useEnrichedAuth();
  const memberMeService = useMemberMeService();
  const location = useLocation();

  const { data: status, isLoading } = useQuery({
    queryKey: ['members', 'me', 'profile-status'],
    queryFn: () => memberMeService.getProfileStatus(),
    enabled: auth.isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });

  // Public visitors fall through — only registered users hit the gate.
  if (!auth.isAuthenticated) return <>{children}</>;

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader />
      </Center>
    );
  }

  if (status && !status.isComplete && location.pathname !== '/auth/onboarding') {
    return <Navigate to="/auth/onboarding" replace />;
  }

  return <>{children}</>;
}
